import { nip57 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';

import { fetchJsonResilient } from '@/lib/resilientFetch';

/**
 * LNURL helpers for the zap flows. LNURL servers frequently omit CORS
 * headers, so all network access goes through resilientFetch (direct first,
 * then public CORS proxies).
 */

export { fetchJsonResilient };

export interface LnurlPayInfo {
  callback: string;
  allowsNostr: boolean;
  nostrPubkey?: string;
  minSendable?: number;
  maxSendable?: number;
}

/**
 * Resolve an author's lightning destination (from their kind 0 metadata) to
 * LNURL-pay parameters, tolerating CORS-hostile endpoints.
 *
 * lud16 (name@domain) is resolved manually with resilient fetching. For
 * lud06-only profiles we fall back to nip57.getZapEndpoint (direct only).
 */
export async function getLnurlPayInfo(
  metadata: { lud16?: string; lud06?: string } | undefined,
  metadataEvent?: NostrEvent,
): Promise<LnurlPayInfo | null> {
  if (!metadata) return null;

  const { lud16, lud06 } = metadata;

  if (lud16) {
    const [name, domain] = lud16.split('@');
    if (name && domain) {
      const url = `https://${domain}/.well-known/lnurlp/${encodeURIComponent(name)}`;
      const json = (await fetchJsonResilient(url)) as Partial<LnurlPayInfo> | null;
      if (json && typeof json.callback === 'string') {
        return {
          callback: json.callback,
          allowsNostr: json.allowsNostr === true,
          nostrPubkey: typeof json.nostrPubkey === 'string' ? json.nostrPubkey : undefined,
          minSendable: typeof json.minSendable === 'number' ? json.minSendable : undefined,
          maxSendable: typeof json.maxSendable === 'number' ? json.maxSendable : undefined,
        };
      }
    }
  }

  // lud06 (bech32-encoded LNURL) — decode+fetch directly via nostr-tools.
  if (lud06 && metadataEvent) {
    try {
      const callback = await nip57.getZapEndpoint(metadataEvent);
      if (callback) {
        return { callback, allowsNostr: true };
      }
    } catch {
      return null;
    }
  }

  return null;
}

/** Build the invoice request URL for a zap (LNURL callback + params). */
export function zapInvoiceUrl(callback: string, millisats: number, signedZapRequest: NostrEvent): string {
  const separator = callback.includes('?') ? '&' : '?';
  return `${callback}${separator}amount=${millisats}&nostr=${encodeURIComponent(JSON.stringify(signedZapRequest))}`;
}
